import { ApiQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { ModuleRendering, SlotRendering, applyModuleRenderableFlags } from '@frontend/common-types';
import { Logger } from '@frontend/logger';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { useAccountRepository, useListRepository } from '@frontend/repository';
import { Spot } from '@frontend/spot/types';
import { useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '../../../redux/store';
import { calculateBoundingBox, calculateScale } from '../../utils';
import { RenderMode } from './spot-layout.component';

interface UseSpotLayoutControllerProps<TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering> {
    spot?: Spot;
    module?: Module;
    renderMode?: RenderMode;
    minScale?: number;
    maxScale?: number;
    fetchSpotData: (arg?: ApiQueryParams<string | number>) => Promise<PaginatedResponse<TModule>>;
    fetchModuleData: (accountId: string, spotId: string, moduleId: string) => Promise<{ results: TSlot[] }>;
    onSelectedModuleChange?: (moduleId: string) => void;
}

export const useSpotLayoutController = <TModule extends ModuleRendering = ModuleRendering, TSlot extends SlotRendering = SlotRendering>(
    props: UseSpotLayoutControllerProps<TModule, TSlot>
) => {
    const { currentAccount } = useAccountRepository();
    const dispatch = useAppDispatch();
    const containerRef = useRef<HTMLDivElement>(null);
    const [initialLoad, setInitialLoad] = useState(true);
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
    const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
    const [selectedModuleSlots, setSelectedModuleSlots] = useState<TSlot[] | null>(null);
    /** Survives per-module slot fetches; seeded from module-api, props.module, and spot rendering. */
    const renderableByIdRef = useRef<Map<string, boolean | undefined>>(new Map());
    const [moduleRenderableById, setModuleRenderableById] = useState<ReadonlyMap<string, boolean | undefined>>(new Map());
    /** Ignores stale slot fetches when the user navigates quickly. */
    const fetchGenerationRef = useRef(0);

    // Stable per SPOT so navigating modules does not drop the spot-rendering cache.
    const listSpotId = props.spot?.id ?? props.module?.spot_id ?? 'default';

    const repository = useListRepository<TModule>({
        fetch: (arg) => props.fetchSpotData(arg),
        dispatch,
        listId: `spot-layout-${listSpotId}`
    });

    const spotIdForFlags = props.spot?.id ?? props.module?.spot_id ?? repository.data?.[0]?.spot_id;

    // Immediate seed for module-mode flows (pick-up confirm, view-content) before module-api returns.
    useEffect(() => {
        if (!props.module || typeof props.module.renderable !== 'boolean') return;
        if (renderableByIdRef.current.get(props.module.id) === props.module.renderable) return;
        renderableByIdRef.current.set(props.module.id, props.module.renderable);
        setModuleRenderableById(new Map(renderableByIdRef.current));
    }, [props.module]);

    // Spot rendering may omit/default `renderable`; prefer the module-api flag.
    useEffect(() => {
        if (!currentAccount || !spotIdForFlags) return;
        let cancelled = false;
        ModuleClient.fetchModules({
            account_id: currentAccount,
            spot_id: spotIdForFlags,
            size: '1000'
        })
            .then((res) => {
                if (cancelled) return;
                for (const module of res.results) {
                    renderableByIdRef.current.set(module.id, module.renderable);
                }
                setModuleRenderableById(new Map(renderableByIdRef.current));
            })
            .catch((error) => {
                Logger.error('Error fetching module renderable flags:', error);
            });
        return () => {
            cancelled = true;
        };
    }, [currentAccount, spotIdForFlags]);

    // Merge explicit booleans from the spot rendering payload into the same cache.
    useEffect(() => {
        if (!repository.data?.length) return;
        let changed = false;
        for (const module of repository.data) {
            if (typeof module.renderable !== 'boolean') continue;
            if (renderableByIdRef.current.get(module.id) === module.renderable) continue;
            renderableByIdRef.current.set(module.id, module.renderable);
            changed = true;
        }
        if (changed) {
            setModuleRenderableById(new Map(renderableByIdRef.current));
        }
    }, [repository.data]);

    const [modules, setModules] = useState<TModule[]>([]);
    useEffect(() => {
        const sorted =
            repository.data?.slice().sort((a, b) => {
                const indexA = a.index || '';
                const indexB = b.index || '';
                return indexA.localeCompare(indexB, undefined, { numeric: true, sensitivity: 'base' });
            }) || [];
        setModules(applyModuleRenderableFlags(sorted, moduleRenderableById) as TModule[]);
    }, [repository.data, moduleRenderableById]);

    // Update container dimensions on mount and resize
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                setDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);

        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    // Auto-select first module when renderMode is 'module'
    useEffect(() => {
        if (props.renderMode === 'module' && modules && modules.length > 0 && (!selectedModuleId || (props.module && props.module.id !== selectedModuleId))) {
            const selected = props.module ? props.module : modules[0];
            handleModuleExpand(selected.spot_id, selected.id, true);
        }

        if (modules && (props.renderMode === 'spot' || !props.renderMode)) {
            setInitialLoad(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.renderMode, modules, props.module]);

    const handleModuleExpand = async (spotId: string, moduleId: string, forceExpand = false) => {
        if (selectedModuleId === moduleId && !forceExpand) {
            // Collapse - go back to all modules (only if not forced and not in module mode)
            if (props.renderMode !== 'module') {
                setSelectedModuleId(null);
                setSelectedModuleSlots(null);
            }
            return;
        }

        // Swap only when ready so the previous module stays visible (no empty white box flash).
        const generation = ++fetchGenerationRef.current;
        try {
            const res = await props.fetchModuleData(currentAccount, spotId, moduleId);
            if (generation !== fetchGenerationRef.current) {
                return;
            }

            setSelectedModuleSlots(res.results);
            setSelectedModuleId(moduleId);
            props.onSelectedModuleChange?.(moduleId);
            setInitialLoad(false);
        } catch (error) {
            if (generation !== fetchGenerationRef.current) {
                return;
            }
            Logger.error('Error fetching module slots:', error);
        }
    };

    const handleNavigateModule = (direction: 'prev' | 'next') => {
        if (!modules || modules.length === 0) return;

        const currentIndex = modules.findIndex((m) => m.id === selectedModuleId);
        if (currentIndex === -1) return;

        const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

        // Don't navigate beyond boundaries
        if (newIndex < 0 || newIndex >= modules.length) return;

        handleModuleExpand(modules[newIndex].spot_id, modules[newIndex].id, true);
    };

    // Determine which modules to render
    const modulesToRender = selectedModuleId ? modules.filter((m) => m.id === selectedModuleId) : modules;

    // Re-apply cached `renderable` when swapping in per-module slots.
    const modulesWithSlots = modulesToRender.map((module) => {
        const cachedRenderable = moduleRenderableById.get(module.id);
        const withFlag = typeof cachedRenderable === 'boolean' ? { ...module, renderable: cachedRenderable } : module;
        if (withFlag.id === selectedModuleId && selectedModuleSlots) {
            return { ...withFlag, slots: selectedModuleSlots as TModule['slots'] };
        }
        return withFlag;
    });

    const bbox = calculateBoundingBox<TModule>(modulesWithSlots);
    const padding = 40;
    const topPadding = 50;
    const scale = calculateScale(bbox.width, bbox.height, dimensions.width, dimensions.height, padding, props.minScale, props.maxScale);

    const containerWidth = bbox.width * scale + padding * 2;
    const containerHeight = bbox.height * scale + padding + topPadding;

    const centerOffsetX = selectedModuleId ? (containerWidth - bbox.width * scale) / 2 - bbox.minX * scale : padding + -bbox.minX * scale;
    const centerOffsetY = selectedModuleId ? (containerHeight - bbox.height * scale) / 2 + bbox.maxY * scale : topPadding + bbox.maxY * scale;

    const currentModule = selectedModuleId ? modules.find((m) => m.id === selectedModuleId) : null;
    const currentModuleIndex = selectedModuleId ? modules.findIndex((m) => m.id === selectedModuleId) : -1;
    const isFirstModule = currentModuleIndex === 0;
    const isLastModule = currentModuleIndex === modules.length - 1;

    useEffect(() => {
        if (containerRef.current) {
            setDimensions({
                width: containerRef.current.offsetWidth,
                height: containerRef.current.offsetHeight
            });
        }
    }, [initialLoad]);

    return {
        containerRef,
        initialLoad,
        data: modules,
        selectedModuleId,
        modules,
        modulesWithSlots,
        currentModule,
        currentModuleIndex,
        isFirstModule,
        isLastModule,
        dimensions,
        scale,
        centerOffsetX,
        centerOffsetY,
        containerWidth,
        containerHeight,
        handleModuleExpand,
        handleNavigateModule,
        renderMode: props.renderMode
    };
};
